import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";

import ConstraintsEditor from "./ConstraintsEditor";
import { ApiContext } from "../../../App";

const BINARY = "BINARY";
const HVAC = "HVAC";
const INPUT = "INPUT";

const KEYS = [
	"TEMPERATURE",
	"SET_POINT",
	"VALVE_POSITION",
	"BATTERY_LEVEL",
	"ON_OFF",
	"MODE",
	"FAN_SPEED",
	"AIR_DIRECTION",
	"PERMIT_PROHIBIT",
	"FILTER_SIGN",
	"FAULT_CODES",
	"POWER_METER",
	"GAS_METER",
	"APPARENT_POWER",
    "VOLTAGE",
    "CURRENT",
    "POWER_FACTOR",
	"HUMIDITY",
	"CO2",
	"PM10",
	"PM2_5",
	"VOC",
	"MOTION",
	"PRESSURE",
	"ELECTRICITY_CONSUMPTION",
	"SOLAR_PRODUCTION",
	"COGENERATION",
	"TRANSFORMER",
];

function General(props) {
	const { value, onChange, errorMessages, tariffs, controllers, floorPlans } =
		props;

	const { t } = useTranslation();

	const handleChange = (props) => {
		onChange(props.target.id, props.target.value);
	};

	const handleTariffChange = (props) => {
		onChange("tariff", props.target.value);
	};

	const handleControllerChange = (props) => {
		onChange("controller", props.target.value);
	};

	const handleFloorPlanChange = (props) => {
		onChange("floor_plan", props.target.value);
	};

	const handleKeyChange = (props) => {
		onChange("key", props.target.value);
	};

	const handleCategoryChange = (props) => {
		onChange("category", props.target.value);
	};

	return value ? (
		<Stack spacing={2}>
			{[HVAC, BINARY].includes(value.category) && (
				<TextField
					variant="standard"
					id="category"
					label={t("Function")}
					onChange={handleCategoryChange}
					value={value.category}
					select
					helperText={
						errorMessages["category"] ? errorMessages["category"] : ""
					}
					error={errorMessages.hasOwnProperty("category")}
				>
					<MenuItem
						key={HVAC}
						value={HVAC}
					>
						{t("Temperature set point")}
					</MenuItem>
					<MenuItem
						key={BINARY}
						value={BINARY}
					>
						{t("Binary")}
					</MenuItem>
				</TextField>
			)}
			{value.category === INPUT && (
				<TextField
					variant="standard"
					id="key"
					label={t("Key")}
					onChange={handleKeyChange}
					value={value.key}
					select
					helperText={errorMessages["key"] ? errorMessages["key"] : ""}
					error={errorMessages.hasOwnProperty("key")}
				>
					{KEYS.map((option) => (
						<MenuItem
							key={option}
							value={option}
						>
							{t(option)}
						</MenuItem>
					))}
				</TextField>
			)}
			<TextField
				variant="standard"
				id="controller"
				label={t("Controller")}
				onChange={handleControllerChange}
				value={value.controller}
				select
				helperText={
					errorMessages["controller"] ? errorMessages["controller"] : ""
				}
				error={errorMessages.hasOwnProperty("controller")}
			>
				{controllers.map((option) => (
					<MenuItem
						key={option.url}
						value={option.url}
					>
						{option.name}
					</MenuItem>
				))}
			</TextField>
			<TextField
				variant="standard"
				id="identifier"
				label={value.category === HVAC ? t("Set point ID") : t("ID")}
				value={value.identifier}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("identifier")}
				helperText={
					errorMessages["identifier"] ? errorMessages["identifier"] : ""
				}
			/>
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="on_off_identifier"
					label={t("On/off ID")}
					value={value.on_off_identifier ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("on_off_identifier")}
					helperText={
						errorMessages["on_off_identifier"]
							? errorMessages["on_off_identifier"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="mode_identifier"
					label={t("Mode ID")}
					value={value.mode_identifier ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("mode_identifier")}
					helperText={
						errorMessages["mode_identifier"]
							? errorMessages["mode_identifier"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="fan_speed_identifier"
					label={t("Fan speed ID")}
					value={value.fan_speed_identifier ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("fan_speed_identifier")}
					helperText={
						errorMessages["fan_speed_identifier"]
							? errorMessages["fan_speed_identifier"]
							: ""
					}
				/>
			)}
			<TextField
				variant="standard"
				id="location"
				label={t("Location")}
				value={value.location}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("location")}
				helperText={errorMessages["location"] ? errorMessages["location"] : ""}
			/>
			<TextField
				variant="standard"
				id="system"
				label={t("System")}
				value={value.system}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("system")}
				helperText={errorMessages["system"] ? errorMessages["system"] : ""}
			/>
			<TextField
				variant="standard"
				id="name"
				label={t("Device")}
				value={value.name}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("name")}
				helperText={errorMessages["name"] ? errorMessages["name"] : ""}
			/>
			<TextField
				variant="standard"
				id="floor_plan"
				label={t("Floor plan")}
				onChange={handleFloorPlanChange}
				value={value.floor_plan ?? ""}
				select
				helperText={
					errorMessages["floor_plan"] ? errorMessages["floor_plan"] : ""
				}
				error={errorMessages.hasOwnProperty("floor_plan")}
			>
				{floorPlans.map((option) => (
					<MenuItem
						key={option.url}
						value={option.url}
					>
						{option.name}
					</MenuItem>
				))}
				{
					<MenuItem
						key={"none"}
						value={""}
					>
						{t("None")}
					</MenuItem>
				}
			</TextField>
			{[HVAC, BINARY].includes(value.category) && (
				<TextField
					variant="standard"
					id="tariff"
					label={t("Tariff")}
					onChange={handleTariffChange}
					value={value.tariff}
					select
					helperText={errorMessages["tariff"] ? errorMessages["tariff"] : ""}
					error={errorMessages.hasOwnProperty("tariff")}
				>
					{tariffs.map((option) => (
						<MenuItem
							key={option.url}
							value={option.url}
						>
							{option.name}
						</MenuItem>
					))}
				</TextField>
			)}
			{[HVAC, BINARY].includes(value.category) && (
				<TextField
					variant="standard"
					id="power_rating"
					label={t("Power rating (kW)")}
					value={value.power_rating}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("power_rating")}
					helperText={
						errorMessages["power_rating"] ? errorMessages["power_rating"] : ""
					}
				/>
			)}
			{[HVAC, BINARY].includes(value.category) && (
				<TextField
					variant="standard"
					id="cycle_duration"
					label={
						value.category === BINARY
							? t("Cycle duration (h)")
							: "Block duration (h)"
					}
					type="number"
					inputProps={{
						maxLength: 13,
						step: "0.5",
					}}
					value={value.cycle_duration ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("cycle_duration")}
					helperText={
						errorMessages["cycle_duration"]
							? errorMessages["cycle_duration"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="set_points"
					label={t("Set points")}
					value={value.set_points ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("set_points")}
					helperText={
						errorMessages["set_points"] ? errorMessages["set_points"] : ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="sensitivity_heating"
					label={t("Sensitivity (heating)")}
					value={value.sensitivity_heating ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("sensitivity_heating")}
					helperText={
						errorMessages["sensitivity_heating"]
							? errorMessages["sensitivity_heating"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="sensitivity_cooling"
					label={t("Sensitivity (cooling)")}
					value={value.sensitivity_cooling ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("sensitivity_cooling")}
					helperText={
						errorMessages["sensitivity_cooling"]
							? errorMessages["sensitivity_cooling"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="sensitivity_off"
					label={t("Sensitivity (off)")}
					value={value.sensitivity_off ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("sensitivity_off")}
					helperText={
						errorMessages["sensitivity_off"]
							? errorMessages["sensitivity_off"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="max_temp_change_heating"
					label={t("Max temp change rate (heating)")}
					value={value.max_temp_change_heating ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("max_temp_change_heating")}
					helperText={
						errorMessages["max_temp_change_heating"]
							? errorMessages["max_temp_change_heating"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="max_temp_change_cooling"
					label={t("Max temp change rate (cooling)")}
					value={value.max_temp_change_cooling ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("max_temp_change_cooling")}
					helperText={
						errorMessages["max_temp_change_cooling"]
							? errorMessages["max_temp_change_cooling"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="input_temp_heating"
					label={t("Input temp (heating)")}
					value={value.input_temp_heating ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("input_temp_heating")}
					helperText={
						errorMessages["input_temp_heating"]
							? errorMessages["input_temp_heating"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="input_temp_cooling"
					label={t("Input temp (cooling)")}
					value={value.input_temp_cooling ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("input_temp_cooling")}
					helperText={
						errorMessages["input_temp_cooling"]
							? errorMessages["input_temp_cooling"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="dead_band"
					label={t("Dead band")}
					value={value.dead_band ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("dead_band")}
					helperText={
						errorMessages["dead_band"] ? errorMessages["dead_band"] : ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="solar_gain_factor"
					label={t("Solar gain factor")}
					value={value.solar_gain_factor ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("solar_gain_factor")}
					helperText={
						errorMessages["solar_gain_factor"]
							? errorMessages["solar_gain_factor"]
							: ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="azimuth"
					label={t("Azimuth")}
					value={value.azimuth ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("azimuth")}
					helperText={errorMessages["azimuth"] ? errorMessages["azimuth"] : ""}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="inclination"
					label={t("Inclination")}
					value={value.inclination ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("inclination")}
					helperText={
						errorMessages["inclination"] ? errorMessages["inclination"] : ""
					}
				/>
			)}
			{value.category === HVAC && (
				<TextField
					variant="standard"
					id="pre_heat"
					label={t("Pre-heat duration (h)")}
					value={value.pre_heat ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("pre_heat")}
					helperText={
						errorMessages["pre_heat"] ? errorMessages["pre_heat"] : ""
					}
				/>
			)}
			{value.category === HVAC && (
				<FormControlLabel
					control={
						<Checkbox
							id="has_heating"
							checked={value.has_heating}
							onChange={(event) => {
								onChange("has_heating", event.target.checked);
							}}
							style={{ paddingLeft: 0, paddingRight: 3 }}
						/>
					}
					label={t("Heating available")}
				/>
			)}
			{value.category === HVAC && (
				<FormControlLabel
					control={
						<Checkbox
							id="has_cooling"
							checked={value.has_cooling}
							onChange={(event) => {
								onChange("has_cooling", event.target.checked);
							}}
							style={{ paddingLeft: 0, paddingRight: 3 }}
						/>
					}
					label={t("Cooling available")}
				/>
			)}
			{[HVAC, BINARY].includes(value.category) && (
				<FormControlLabel
					control={
						<Checkbox
							id="managed"
							checked={value.managed}
							onChange={(event) => {
								onChange("managed", event.target.checked);
							}}
							style={{ paddingLeft: 0, paddingRight: 3 }}
						/>
					}
					label={t("Automated")}
				/>
			)}
			<FormControlLabel
					control={
						<Checkbox
							id="display_on_floorplan"
							checked={value.display_on_floorplan}
							onChange={(event) => {
								onChange("display_on_floorplan", event.target.checked);
							}}
							style={{ paddingLeft: 0, paddingRight: 3 }}
						/>
					}
					label={t("Display on floor plan")}
			/>
		</Stack>
	) : null;
}

function PointEditPopUp(props) {
	const { open, onCloseWithoutSaving, onCloseWithUpdate } = props;

	const { t } = useTranslation();

	const [activeTab, setActiveTab] = React.useState("general");
	const [data, setData] = React.useState(props.data);
	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [errorMessages, setErrorMessages] = React.useState({});
	const [tariffs, setTariffs] = React.useState(props.tariffs);
	const [controllers, setControllers] = React.useState(props.controllers);
	const [floorPlans, setFloorPlans] = React.useState(props.floorPlans);

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const myRef = React.useRef();

	const {pointsApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		let data_copy = props.data;
		if (data_copy.must_be_set_to_constraints)
			data_copy.must_be_set_to_constraints =
				data_copy.must_be_set_to_constraints.map((item) => ({
					...item,
					id: uuidv4(),
				}));
		if (data_copy.minimum_utilization_constraints)
			data_copy.minimum_utilization_constraints =
				data_copy.minimum_utilization_constraints.map((item) => ({
					...item,
					id: uuidv4(),
				}));
		if (data_copy.temperature_constraints)
			data_copy.temperature_constraints = data_copy.temperature_constraints.map(
				(item) => ({ ...item, id: uuidv4() })
			);
		setData(data_copy);
	}, [props.data]);

	React.useEffect(() => {
		setTariffs(props.tariffs);
	}, [props.tariffs]);

	React.useEffect(() => {
		setControllers(props.controllers);
	}, [props.controllers]);

	React.useEffect(() => {
		setFloorPlans(props.floorPlans);
	}, [props.floorPlans]);

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	const handleFormChange = (id, value) => {
		if (id === "category") {
			if (value === HVAC) {
				setData((old) => ({
					...old,
					[id]: value,
					minimum_utilization_constraints: null,
					temperature_constraints: [],
					sensitivity_heating: 1.0,
					sensitivity_cooling: 1.0,
					sensitivity_off: 0.1,
					max_temp_change_heating: 5,
					max_temp_change_cooling: 5,
					input_temp_heating: 30,
					input_temp_cooling: 16,
					dead_band: 0.25,
					pre_heat: "2.0",
					has_heating: true,
					has_cooling: true,
					set_points: "",
					key: null,
					on_off_identifier: "",
					mode_identifier: "",
					fan_speed_identifier: "",
					azimuth: 0,
					inclination: 0,
					solar_gain_factor: 0.0,
					display_on_floorplan: false
				}));
			} else if (value === BINARY) {
				setData((old) => ({
					...old,
					[id]: value,
					minimum_utilization_constraints: [],
					temperature_constraints: null,
					sensitivity_heating: null,
					sensitivity_cooling: null,
					sensitivity_off: null,
					max_temp_change_heating: null,
					max_temp_change_cooling: null,
					input_temp_heating: null,
					input_temp_cooling: null,
					dead_band: null,
					pre_heat: null,
					has_heating: null,
					has_cooling: null,
					set_points: null,
					key: null,
					on_off_identifier: null,
					mode_identifier: null,
					fan_speed_identifier: null,
					azimuth: null,
					inclination: null,
					solar_gain_factor: null,
					display_on_floorplan: false
				}));
			}
		} else {
			setData((old) => ({ ...old, [id]: value }));
		}

		setErrorMessages((old) =>
			Object.fromEntries(Object.entries(old).filter(([key]) => key !== id))
		);
	};

	const handleCloseWithoutSaving = () => {
		setData(props.data);
		onCloseWithoutSaving();
		setErrorMessages({});
	};

	const handleSave = () => {
		setErrorMessages({});
		
		var newData = { ...data };
		if ([HVAC, BINARY].includes(data.category)) {
			const constraintsData = myRef.current.getConstraints();
			newData = {
				...data,
				must_be_set_to_constraints: constraintsData.mustBeSetTo,
				minimum_utilization_constraints: constraintsData.minUtil,
				temperature_constraints: constraintsData.temperature,
			};
		}

		const onSuccess = (data) => {
			const updatedRow = { ...newData, id: data.url };
			onCloseWithUpdate(updatedRow);
		};

		const onError = (response) => {

			setErrorMessages(response);

			const non_field_errors = Object.entries(response)
				.filter(([key]) => key === "non_field_errors")
				.map(([key, value]) => value);
			if (non_field_errors.length > 0) {
				setAlertMessage(non_field_errors.join("; "));
				setAlertOpen(true);
			}

			if (
				response["minimum_utilization_constraints"] ||
				response["temperature_constraints"] ||
				response["must_be_set_to_constraints"]
			)
				setActiveTab("constraints");
			else setActiveTab("general");
		};

		if (!data.hasOwnProperty("id")) {
			let payload = { ...newData };

			pointsApi.create(payload, onSuccess, onError);
		} else {
			let payload = { ...newData };
			delete payload.id;
			console.log(payload);
			pointsApi.update(newData.id, payload, onSuccess, onError);
		}
	};

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleCloseWithoutSaving}
				fullScreen={fullScreen}
				fullWidth
				maxWidth={"md"}
			>
				<DialogTitle>{t("Edit point")}</DialogTitle>
				<DialogContent>
					{[HVAC, BINARY].includes(data.category) && (
						<Box
							sx={{ borderBottom: 1, marginBottom: 3, borderColor: "divider" }}
						>
							<Tabs
								value={activeTab}
								onChange={handleTabChange}
							>
								<Tab
									label="General"
									value="general"
								/>
								{[HVAC, BINARY].includes(data.category) && (
									<Tab
										label="Constraints"
										value="constraints"
									/>
								)}
							</Tabs>
						</Box>
					)}

					{activeTab === "general" ? (
						<General
							value={data}
							onChange={handleFormChange}
							errorMessages={errorMessages}
							tariffs={tariffs}
							controllers={controllers}
							floorPlans={floorPlans}
						/>
					) : null}
					<Box sx={{ display: activeTab === "constraints" ? "block" : "none" }}>
						<Box
							height="calc(100vh - 300px)"
							maxHeight={1220}
							minWidth={700}
						>
							<ConstraintsEditor
								setPoints={data.set_points}
								category={data.category}
								mustBeSetTo={data.must_be_set_to_constraints}
								minUtil={data.minimum_utilization_constraints}
								temperatureConstr={data.temperature_constraints}
								errorMessages={errorMessages}
								ref={myRef}
								onChange={() => {}}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCloseWithoutSaving}
						autoFocus
					>
						{t("Cancel")}
					</Button>
					<Button onClick={handleSave}>{t("Save")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">
					{t("Problem saving data")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}

export default PointEditPopUp;
